import { InvokeCommand, type InvokeCommandInput, LambdaClient } from '@aws-sdk/client-lambda';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { type JWT } from 'aws-amplify/auth';
import { saveAs } from 'file-saver';
import { v4 as uuid } from 'uuid';
import { getIdToken } from '@/auth';
import Log from '@/Logger';
import { splitObjectKey } from '../util/splitObjectKey';
import { getObjectAsBlob } from '@/feature/api-client/s3';

function getMetadata(resp: any) {
    return resp?.['$metadata'];
}

export const handleClickDownload = async (key: string) => {
    const logger = Log.getLogger();
    try {
        const blob = await getObjectAsBlob(key); // throws S3ClientError

        const fileName = splitObjectKey(key).documentName;

        saveAs(blob, fileName);
    } catch (err) {
        logger.api.error('S3', 'Fail to request: GetObjectCommand', err);
    }
};

export const handleClickDownloadAll = async (keys: string[]) => {
    for (let key of keys) {
        handleClickDownload(key);
    }
};

/**
* Lambda関数を呼び出してkintoneアプリにレコード登録を行う非同期関数。
*
* 指定されたパラメータを元にkintoneレコード登録用のLambda関数を呼び出します。
* Lambdaからのレスポンスのステータスが 'success' でない場合、falseを返し、エラー時もfalseを返します。
*
* @param presignURL - 生成されたpresigned URL。
* @param kintoneDomain - kintoneのドメイン。
* @param kintoneAppId - kintoneアプリのID。
* @param kintoneTokenType - kintone認証トークンの種類。
* @param kintoneAccessToken - kintoneアクセストークン。
* @returns レコード登録が成功した場合はtrue、失敗した場合はfalseを返す。
*/
export const registerRecord = async (
    presignedURL: string,
    kintoneDomain: string,
    kintoneAppId: string,
    kintoneTokenType: string,
    kintoneAccessToken: string
): Promise<boolean> => {
    // Lambda関数に渡す引数オブジェクトを作成
    const argString: RegisterRecordArg = {
        pdfUrl: presignedURL,
        kintoneDomain,
        kintoneAppId,
        kintoneTokenType,
        kintoneAccessToken,
    };

    const logger = Log.getLogger();
    try {
        // Lambdaクライアントの作成
        const client = new LambdaClient(await getConf());

        // Lambda関数呼び出しコマンドの構築と送信
        const command = new InvokeCommand({
            FunctionName: import.meta.env.VITE_AWS_RECORDREGISTER_ARN,
            Payload: new TextEncoder().encode(JSON.stringify(argString)),
        } as InvokeCommandInput);

        logger.api.info('Lambda', 'Start to request: kintone-formpdfrecordregister');
        const response = await client.send(command);
        logger.api.info('Lambda', 'Succeed to request: kintone-formpdfrecordregister');

        // レスポンスのデコードとJSONパース
        const payload = JSON.parse(new TextDecoder().decode(response.Payload));

        // Lambdaからのレスポンスステータスを判定
        if (payload.status !== 'success') {
            logger.api.info('Lambda', 'Fail to register record: kintone-formpdfrecordregister', getMetadata(response));
            return false;
        }
        return true;
    } catch (err) {
        logger.api.error('Lambda', 'Fail to request: kintone-formpdfrecordregister', err);
        return false;
    }
};

const getConf = async () => {
    const logger = Log.getLogger();
    try {
        logger.api.info('Cognito', 'Start to request: currentSession');
        const token = await getIdToken();
        logger.api.info('Cognito', 'Succeed to request: currentSession');

        const conf = {
            region: import.meta.env.VITE_AWS_REGION,
            credentials: fromCognitoIdentityPool({
                clientConfig: { region: import.meta.env.VITE_AWS_REGION },
                identityPoolId: import.meta.env.VITE_AWS_IDENTITY_POOL_ID as string,
                logins: {
                    ['cognito-idp.ap-northeast-1.amazonaws.com/' + import.meta.env.VITE_AWS_USER_POOL_ID as string]: token.toString(),
                },
            })
        }
        return conf;
    } catch (err) {
        logger.api.error('Cognito', 'Fail to request: currentSession', err);
        throw err;
    }
}

export const getConf_S3 = async () => {
    const logger = Log.getLogger();
    try {
        logger.api.info('Cognito', 'Start to request: currentSession');
        const token = await getIdToken();
        logger.api.info('Cognito', 'Succeed to request: currentSession');

        const _resp = await _InvokeCommand(token);

        return {
            region: import.meta.env.VITE_AWS_REGION,
            credentials: {
                accessKeyId: _resp.Credentials?.AccessKeyId!,
                secretAccessKey: _resp.Credentials?.SecretAccessKey!,
                sessionToken: _resp.Credentials?.SessionToken!,
            },
        };
    } catch (err) {
        logger.api.error('Cognito', 'Fail to request: currentSession', err);
        throw err;
    }
}

async function _InvokeCommand(token: JWT) {
    const logger = Log.getLogger();
    try {
        const client = new LambdaClient(await getConf());
        logger.api.info('Lambda', 'Start to request: InvokeCommand');
        const command = new InvokeCommand({
            FunctionName: import.meta.env.VITE_AWS_S3_TOKEN,
            Payload: new TextEncoder().encode(`{ "requestId": "${uuid()}", "token": "${token.toString()}" }`)
        } as InvokeCommandInput);
        const response = await client.send(command);
        logger.api.info('Lambda', 'Succeed to request: InvokeCommand', getMetadata(response));

        return JSON.parse(new TextDecoder().decode(response.Payload));
    } catch (err) {
        logger.api.error('Lambda', 'Fail to request: InvokeCommand', err);
    }
}

/**
 * kintoneレコード登録に必要な引数の型定義。
 *
 * @property pdfUrl - PDFファイルのURL。
 * @property kintoneDomain - kintoneのドメイン。
 * @property kintoneAppId - kintoneアプリのID。
 * @property kintoneTokenType - kintone認証トークンの種類。
 * @property kintoneAccessToken - kintoneアクセストークン。
 */
type RegisterRecordArg = {
    pdfUrl: string;
    kintoneDomain: string;
    kintoneAppId: string;
    kintoneTokenType: string;
    kintoneAccessToken: string;
};
